import React, { useEffect, useMemo, useState } from "react";
import { MdClose, MdPerson } from "react-icons/md";
import api from "../../../../services/axios";
import Modal from "../../../../components/modal";
import { EmployeeHours } from "../../../../interfaces/Financial";

interface Props {
  projectId: number;
  projectName: string;
  month: number;
  year: number;
  onClose: () => void;
}

const fmtHours = (h: number) =>
  `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;

const ProjectReportModal: React.FC<Props> = ({ projectId, projectName, month, year, onClose }) => {
  const [rows, setRows] = useState<EmployeeHours[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get<EmployeeHours[]>("/Financial/EmployeeHours", {
          params: { month, year, projectId },
        });
        if (active) setRows(res.data);
      } catch {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [projectId, month, year]);

  const total = useMemo(() => rows.reduce((s, r) => s + r.totalHours, 0), [rows]);
  const sorted = useMemo(() => [...rows].sort((a, b) => b.totalHours - a.totalHours), [rows]);
  const maxHours = sorted[0]?.totalHours ?? 0;

  const monthName = new Date(year, month - 1, 1).toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });

  return (
    <Modal onClose={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-dv-lg dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between bg-ink-900 px-5 py-4 text-white dark:bg-navy-900">
          <div>
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">Report de horas · projeto</p>
            <h3 className="text-lg font-bold leading-tight">{projectName}</h3>
            <p className="text-xs capitalize text-white/70">{monthName}</p>
          </div>
          <button aria-label="Fechar" onClick={onClose} className="rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white">
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="border-b border-gray-100 px-5 py-3 dark:border-gray-700">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Total no mês</span>
            <span className="font-display text-2xl font-bold tabular-nums tracking-tight text-navy-700 dark:text-white">{fmtHours(total)}</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-3">
          {loading ? (
            <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Carregando...</p>
          ) : sorted.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Nenhuma hora apontada neste período.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {sorted.map((r) => (
                <div key={r.employeeId}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-medium text-navy-700 dark:text-white">
                      <MdPerson className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      {r.employeeName}
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-navy-700 dark:text-white">{fmtHours(r.totalHours)}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-dv-blue-500 to-dv-cyan-400 transition-all duration-500"
                      style={{ width: `${maxHours > 0 ? (r.totalHours / maxHours) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProjectReportModal;
